// Troca automática entre janela principal e barra flutuante (P0.4).
//  - iniciar tarefa com "abrir automaticamente" ligado → barra aparece, principal minimiza
//  - sem tarefa rodando (e sem pausa do Pomodoro) → barra some, principal volta
//  - finalizar → a barra cresce com "Feito!", meme e confetes por 2,5 s (AC-046 / ASM-023);
//    com "Comemoração" desligada, só "Feito!" verde no lugar do título por 0,5 s (AC-230)
//  - hora de relaxar do Pomodoro → a barra fica crescida com o tomate e "Vamos relaxar";
//    depois do meme, ele sai para a esquerda e dá lugar ao botão (AC-189)
// Na web não há ponte (bridge = null): nada é aberto nem minimizado (AC-162).
import type { PomodoroPhase, PomodoroState } from '../domain/pomodoro';
import type { DesktopBridge, FloatCommand, FloatSnapshot } from '../platform/desktop';
import { pickMeme } from '../ui/memes';
import type { AppStore } from './AppStore';

/** comemoração com meme e confetes (AC-031 / AC-046) */
export const CELEBRATE_MS = 2500;
/** "Feito!" verde com a comemoração desligada (AC-230) */
export const DONE_FLASH_MS = 500;

export function shouldShowFloat(input: { running: boolean; pomodoroPhase: PomodoroPhase; celebrating: boolean }): boolean {
  return input.running || input.celebrating || input.pomodoroPhase === 'ready' || input.pomodoroPhase === 'break' || input.pomodoroPhase === 'breakOver';
}

export interface FloatControllerDeps {
  store: AppStore;
  bridge: Pick<
    DesktopBridge,
    'showFloat' | 'hideFloat' | 'setFloatExpanded' | 'minimizeMain' | 'restoreMain' | 'sendToFloat' | 'onFloatCommand'
  > | null;
  pomodoro?: { get(): PomodoroState; subscribe(l: () => void): () => void; resume(): Promise<void>; skipBreak(): void; relax(): void } | null;
  theme?: () => 'light' | 'dark';
  language?: () => string;
}

export function createFloatController({ store, bridge, pomodoro, theme, language }: FloatControllerDeps) {
  if (!bridge) {
    return { openFloat: async () => {}, isVisible: () => false, subscribe: (_l: () => void) => () => {}, dispose: () => {} };
  }
  const b = bridge;
  let visible = false;
  /** a barra está crescida (300 × 260): comemoração ou hora de relaxar */
  let expanded = false;
  const visibleListeners = new Set<() => void>();
  let celebrating: FloatSnapshot['celebrating'] = null;
  /** finalização em andamento: segura a barra até a comemoração começar */
  let finishing = false;
  /** voltando da pausa do Pomodoro: segura a barra até a próxima tarefa iniciar (sem piscar a lista) */
  let resuming = false;
  let celebrateTimer: ReturnType<typeof setTimeout> | null = null;
  const unsubs: Array<() => void> = [];

  const phase = (): PomodoroPhase => pomodoro?.get().phase ?? 'idle';
  const runningTask = () => store.get().tasks.find((t) => t.status === 'running') ?? null;
  const pomodoroTask = () => {
    const id = pomodoro?.get().taskId;
    return id ? store.get().tasks.find((t) => t.id === id) ?? null : null;
  };

  const snapshot = (): FloatSnapshot => ({
    task: runningTask() ?? (phase() !== 'idle' ? pomodoroTask() : null),
    offline: !store.get().online,
    pomodoro: pomodoro?.get() ?? null,
    celebrating,
    clockOffset: store.get().clockOffset,
    language: language?.() ?? 'pt-BR',
    theme: theme?.() ?? 'dark',
  });

  const push = () => (visible ? b.sendToFloat(snapshot()) : Promise.resolve());

  async function setExpanded(want: boolean) {
    if (want === expanded) return;
    expanded = want;
    await b.setFloatExpanded(want);
  }

  async function show() {
    if (visible) return push();
    visible = true;
    visibleListeners.forEach((l) => l());
    await b.showFloat();
    await b.minimizeMain();
    await push();
  }

  async function hide() {
    if (!visible) return;
    visible = false;
    await setExpanded(false);
    await b.hideFloat();
    await b.restoreMain();
    visibleListeners.forEach((l) => l());
  }

  async function evaluate() {
    if (!visible || resuming) return;
    if (!shouldShowFloat({ running: !!runningTask(), pomodoroPhase: phase(), celebrating: !!celebrating || finishing })) return hide();
    await setExpanded((!!celebrating && !celebrating.quiet) || phase() === 'ready');
    await push();
  }

  unsubs.push(
    store.on((e) => {
      if (e.type === 'started') {
        // a próxima iniciada pelo Pomodoro no meio do bloco não corta o "Feito!" (AC-071)
        if (!(celebrating && e.source === 'pomodoro')) {
          celebrating = null;
          if (celebrateTimer) clearTimeout(celebrateTimer);
        }
        if (store.get().profile?.float_auto_open !== false || visible) void show().then(evaluate);
      }
      if (e.type === 'finishing' && visible) finishing = true;
      if (e.type === 'finishFailed') {
        finishing = false;
        void evaluate();
      }
      if (e.type === 'finished' && finishing) {
        finishing = false;
        const c = store.get().celebration;
        const quiet = store.get().profile?.celebration_enabled === false;
        celebrating = { title: c?.title ?? '', result: c?.result ?? { kind: 'noEstimate', minutes: 0 }, meme: pickMeme(), quiet };
        void setExpanded(!quiet || phase() === 'ready').then(push);
        if (celebrateTimer) clearTimeout(celebrateTimer);
        // depois do meme: encolhe — ou, na hora de relaxar, continua crescida com "Vamos relaxar"
        celebrateTimer = setTimeout(
          () => {
            celebrating = null;
            void evaluate();
          },
          quiet ? DONE_FLASH_MS : CELEBRATE_MS
        );
      }
    })
  );
  /** "Voltar ao foco" na barra: inicia direto; só volta à lista se não houver o que iniciar. */
  async function resumeFromBreak() {
    resuming = true;
    try {
      await pomodoro!.resume();
    } finally {
      resuming = false;
    }
    await evaluate();
  }

  unsubs.push(store.subscribe(() => void evaluate()));
  if (pomodoro) unsubs.push(pomodoro.subscribe(() => void evaluate()));

  const handle = (cmd: FloatCommand) => {
    switch (cmd.type) {
      case 'pause':
        return void store.pause(cmd.id, 'float');
      case 'resume':
        return pomodoro && pomodoro.get().phase !== 'idle' && pomodoro.get().phase !== 'focus'
          ? void resumeFromBreak()
          : void store.start(cmd.id, 'float');
      case 'start':
        return void store.start(cmd.id, 'float');
      case 'finish':
        return void store.finish(cmd.id, 'float');
      case 'skipBreak':
        return pomodoro?.skipBreak();
      case 'relax':
        return pomodoro?.relax();
      case 'open':
        celebrating = null;
        return void hide();
      case 'ready':
        return void push();
    }
  };
  let offCommands: (() => void) | null = null;
  let disposed = false;
  // o registro é assíncrono: se o controlador já foi descartado (o StrictMode cria e descarta
  // um), cancela na hora — senão os dois atendem e "Finalizar" conclui a tarefa duas vezes
  void b.onFloatCommand(handle).then((un) => (disposed ? un() : (offCommands = un)));

  return {
    /** botão "mini janela" / ícone de recolher */
    openFloat: show,
    isVisible: () => visible,
    /** avisa quando a barra abre ou fecha (a lista mostra a comemoração ao voltar — AC-190) */
    subscribe(l: () => void) {
      visibleListeners.add(l);
      return () => visibleListeners.delete(l);
    },
    handle,
    dispose() {
      disposed = true;
      unsubs.forEach((u) => u());
      offCommands?.();
      if (celebrateTimer) clearTimeout(celebrateTimer);
    },
  };
}

export type FloatController = ReturnType<typeof createFloatController>;
